import { getGridSignificanceRowKey, GRID_SEGMENT_ACCESSOR } from './gridSignificanceAdapter';
import styles from '../../../utilities/significanceColumns.module.scss';

const renderDefaultCell = (value) => (value === undefined ? '' : value);

/**
 * Adds segment-significance notation to grid-shaped table columns: a lead letter
 * in the compare-by column and superscript letters (plus tint) in option cells.
 *
 * @param {Object} options
 * @param {Array<Object>} options.columns - Table column definitions to transform.
 * @param {Object|null} options.significance - Result of a grid-shaped significance adapter.
 * @param {Function} [options.getRowKey] - Resolves a row's key into the significance maps;
 *   defaults to the grid statement-and-segment key.
 * @returns {Array<Object>} The transformed columns, or the original columns when unavailable.
 */
export const applyGridSignificanceColumns = ({
  columns,
  getRowKey = getGridSignificanceRowKey,
  significance,
}) => {
  if (!significance || !columns?.length) return columns;
  const { cellHigherThanByKey, optionAccessors, rowLetterByKey } = significance;

  return columns.map((column) => {
    if (column.accessor === GRID_SEGMENT_ACCESSOR) {
      const originalCell = column.Cell;
      const letterFor = (rowOriginal) => rowLetterByKey?.[getRowKey(rowOriginal)];
      return {
        ...column,
        Cell: (cellProps) => {
          const base = originalCell ? originalCell(cellProps) : renderDefaultCell(cellProps?.value);
          const letter = letterFor(cellProps?.row?.original);
          if (!letter) return base;
          return (
            <span className={styles['significance-cell']}>
              <span
                aria-label={`significance letter ${letter}`}
                className={styles['significance-letter-lead']}
                data-letters={`(${letter})`}
                data-testid="grid-significance-segment-letter"
              />
              {base}
            </span>
          );
        },
        formatCopyValue: (rowOriginal, formattedValue) => {
          const letter = letterFor(rowOriginal);
          return letter ? `(${letter}) ${formattedValue}` : formattedValue;
        },
      };
    }

    if (!optionAccessors.includes(column.accessor)) return column;

    const originalCell = column.Cell;
    const lettersFor = (rowOriginal) =>
      cellHigherThanByKey?.[getRowKey(rowOriginal)]?.[column.accessor] ?? [];

    return {
      ...column,
      Cell: (cellProps) => {
        const base = originalCell ? originalCell(cellProps) : renderDefaultCell(cellProps?.value);
        const letters = lettersFor(cellProps?.row?.original);
        if (!letters.length) return base;
        return (
          <span className={styles['significance-cell']}>
            {base}
            <sup
              aria-label={`significantly higher than ${letters.join(', ')}`}
              className={styles['significance-letters']}
              data-letters={letters.join(letters.some((l) => l.length > 1) ? ' ' : '')}
              data-testid="grid-significance-letters"
            />
          </span>
        );
      },
      formatCopyValue: (rowOriginal, formattedValue) => {
        const letters = lettersFor(rowOriginal);
        if (!letters.length) return formattedValue;
        return `${formattedValue} ${letters.join(letters.some((l) => l.length > 1) ? ' ' : '')}`;
      },
      getCellHighlight: ({ row }) => (lettersFor(row).length ? 'success' : undefined),
    };
  });
};
